import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import {
  Destroyable,
  isSomething,
  SaveForm,
  untilDestroy,
  ParsedGooglePlace,
  countries,
  selectSome,
  selectSomeOnce,
} from '@tokeet-frontend/tv3-platform'
import { selectGuestById } from '@tv3/store/guest/guest.selectors'
import {
  AddGuestEmail,
  AddGuestPhone,
  DeleteGuestEmail,
  DeleteGuestPhone,
  UpdateGuest,
  UpdateGuestItem,
  AddGuest,
} from '@tv3/store/guest/guest.actions'
import { Guest } from '@tv3/store/guest/guest.model'
import { MultipleItemsDialogService } from '@tv3/containers/guests/edit/multiple-items/multiple-items-dialog.service'
import * as R from 'ramda'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { CreateGuestRequest } from '@tv3/interfaces/requests/guest.request'
import { InitialWhatsappDialogService } from '@tv3/containers/messages/initial-whatsapp-dialog/initial-whatsapp-dialog.service'

@Component({
  selector: 'app-guest-dialog',
  templateUrl: './guest-dialog.component.html',
  host: { class: 'modal-content' },
  styleUrls: ['./guest-dialog.component.scss'],
})
export class GuestDialogComponent extends Destroyable implements OnInit {
  guestForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    name: ['', [Validators.required]],
    phone: [],
    whatsapp_phone: [],
    address: this.fb.group({
      address: [''],
      city: [''],
      state: [''],
      country: [],
      country_code: [],
      postal_code: [''],
    }),
  })

  isEdit = false
  guest: Guest

  get addressForm() {
    return this.guestForm.get('address') as FormGroup
  }

  constructor(
    public dialogRef: MatDialogRef<GuestDialogComponent>,
    private fb: FormBuilder,
    private store: Store<fromRoot.State>,
    private amplitudeService: AmplitudeService,
    private initialWhatsappDialogService: InitialWhatsappDialogService,
    @Inject(MAT_DIALOG_DATA) public data: { guestId?: string },
    private multipleItemsDialogService: MultipleItemsDialogService
  ) {
    super()
  }

  ngOnInit() {
    if (isSomething(this.data.guestId)) {
      this.isEdit = true
      this.store.pipe(selectSome(selectGuestById, { id: this.data.guestId }), untilDestroy(this)).subscribe((guest) => {
        this.setGuestForm(guest)
        this.guest = guest
      })
    }
  }

  onSendWhatsappMessage() {
    this.initialWhatsappDialogService.open({
      guest: { ...this.guest, ...this.guestForm.getRawValue() },
    })
  }

  setGuestForm(guest: Guest) {
    const address = guest.address
    this.guestForm.patchValue(
      {
        name: guest.name,
        email: guest.primaryEmail || '',
        phone: guest.phone,
        whatsapp_phone: guest.whatsapp_phone,
        address: {
          address: address?.address,
          city: address?.city,
          country: address?.country,
          country_code: address?.countryCode,
          state: address?.state,
          postal_code: address?.postalCode,
        },
      },
      { emitEvent: false }
    )
  }

  onPlaceChange(parsedPlace: ParsedGooglePlace) {
    const country = R.find((c) => c.id === parsedPlace.countryShort, countries)
    this.addressForm.patchValue({
      address: (parsedPlace.streetNumber ? parsedPlace.streetNumber + ' ' : '') + (parsedPlace.street || ''),
      city: parsedPlace.city,
      state: parsedPlace.state,
      country: country?.name,
      country_code: country?.id,
      postal_code: parsedPlace.postCode,
    })
  }

  close() {
    this.dialogRef.close()
  }

  @SaveForm()
  save(form: FormGroup) {
    if (!this.isEdit) {
      const values = form.getRawValue() as CreateGuestRequest
      this.amplitudeService.logEvent('add-guest')
      this.store.dispatch(AddGuest({ form: { ...values, source: 'tokeet' } }))
    } else {
      this.amplitudeService.logEvent('edit-guest')
      this.store.dispatch(
        UpdateGuest({
          guestId: this.data.guestId,
          form: form.getRawValue(),
        })
      )
    }

    this.close()
  }

  editEmail() {
    if (!this.guest) return
    const config = {
      height: 'auto',
      data: {
        type: 'email',
        label: $localize`Edit Guest Emails`,
        itemLabel: 'Email',
        newItemLabel: 'Add email',
        primaryItem: () => this.guest.primaryEmailView,
        items: () => Object.keys(this.guest.emailView),
        makePrimary: (item) => {
          this.store.dispatch(UpdateGuestItem({ guestId: this.guest.id, data: { email: item } }))
        },
        makePrimaryLabel: `Make this email the guest's primary email.`,
        deleteItem: (item) => {
          this.store.dispatch(DeleteGuestEmail({ guestId: this.guest.id, data: { email: item } }))
        },
        addItems: (items) => {
          this.store.dispatch(AddGuestEmail({ guestId: this.guest.id, data: items }))
        },
        validators: [Validators.email],
        icon: 'fal fa-user-edit',
      },
    }
    this.multipleItemsDialogService.open(config)
  }

  editPhone() {
    if (!this.guest) return
    const config = {
      height: 'auto',
      data: {
        type: 'phone',
        label: $localize`Edit Guest Phones`,
        itemLabel: 'Phone',
        newItemLabel: 'Add phone',
        primaryItem: () => this.guest.phone,
        items: () =>
          R.pipe(
            (arr: string[]) => (this.guest.phone ? [...arr, this.guest.phone] : arr),
            R.map(String),
            R.uniq
          )(this.guest.phones),
        makePrimary: (item) => {
          this.store.dispatch(
            UpdateGuestItem({
              guestId: this.guest.id,
              data: {
                phone: item,
                phones: [...this.guest.phones, this.guest.phone].filter((phone) => {
                  return isSomething(phone) && phone !== item
                }),
              },
            })
          )
        },
        makePrimaryLabel: $localize`Make this phone number this guest's primary phone.`,
        deleteItem: (item) => {
          this.store.dispatch(DeleteGuestPhone({ guestId: this.guest.id, data: { phone: item } }))
        },
        addItems: (items) => {
          this.store.dispatch(AddGuestPhone({ guestId: this.guest.id, data: items }))
        },
        icon: 'fal fa-user-edit',
      },
    }
    this.multipleItemsDialogService.open(config)
  }
}
